<script setup>
import { ref, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: File,
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const fileInput = ref(null);
const dragover = ref(false);
const imagePreview = ref(null);

// Watch for modelValue changes to update preview
watch(
  () => props.modelValue,
  newVal => {
    if (!newVal) {
      imagePreview.value = null;
      return;
    }

    const reader = new FileReader();
    reader.onload = e => {
      imagePreview.value = e.target.result;
    };
    reader.readAsDataURL(newVal);
  },
  { immediate: true }
);

const processFile = file => {
  if (!file.type.startsWith('image/')) {
    useAlert(t('GENERAL.IMAGE_UPLOAD.NON_IMAGE_ERROR'));
    return;
  }
  emit('update:modelValue', file);
};

const handleDrop = e => {
  const files = e.dataTransfer.files;
  if (files.length) processFile(files[0]);
  dragover.value = false;
};

const handleFileUpload = e => {
  const file = e.target.files[0];
  if (file) processFile(file);
};

const removeImage = () => {
  emit('update:modelValue', null);
  fileInput.value.value = null;
};
</script>

<template>
  <div class="">
    <div
      v-if="!modelValue"
      class="w-full h-64 border-2 border-dashed bg-slate-800 border-woot-500 rounded-lg flex flex-col items-center justify-center transition-colors"
      :class="[
        dragover
          ? 'border-blue-500 bg-blue-50'
          : 'border-gray-300 hover:border-gray-400',
      ]"
      @dragover.prevent="dragover = true"
      @dragleave.prevent="dragover = false"
      @drop.prevent="handleDrop"
    >
      <input
        ref="fileInput"
        type="file"
        class="hidden"
        accept="image/*"
        @change="handleFileUpload"
      />

      <div class="text-center space-y-2">
        <div class="flex w-full justify-center">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M24 21.3333V2.66667C24 1.2 22.8 0 21.3333 0H2.66667C1.2 0 0 1.2 0 2.66667V21.3333C0 22.8 1.2 24 2.66667 24H21.3333C22.8 24 24 22.8 24 21.3333ZM7.33333 14L10.6667 18.0133L15.3333 12L21.3333 20H2.66667L7.33333 14Z"
            />
          </svg>
        </div>
        <p class="text-gray-600">{{ $t('GENERAL.IMAGE_UPLOAD.HINT') }}</p>
        <woot-button
          button-class="mt-2 inline-flex justify-center"
          @click="fileInput?.click()"
        >
          {{ $t('GENERAL.IMAGE_UPLOAD.UPLOAD_BUTTON') }}
        </woot-button>
      </div>
    </div>

    <div v-else class="relative w-full h-64 rounded-lg overflow-hidden">
      <img
        :src="imagePreview"
        alt="Uploaded image"
        class="w-full h-full object-cover"
      />
      <button
        class="absolute bottom-0 left-0 right-0 w-full py-2 bg-red-500/90 text-white hover:bg-red-600 transition-colors"
        @click="removeImage"
      >
        {{ $t('GENERAL.IMAGE_UPLOAD.REMOVE_BUTTON') }}
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
svg {
  --color-woot-500: theme('colors.woot.500');
  fill: var(--color-woot-500);
}

.border-woot-500 {
  --color-woot-500: theme('colors.woot.500');
  border-color: var(--color-woot-500);
}
</style>
